import { type ReactNode } from 'react';
import { Radio, Database, FileText, FlaskConical, ArrowUpRight } from 'lucide-react';
import { Badge, type BadgeTone } from '../../ui';
import './LabsSettings.css';

type FeatureStatus = 'alpha' | 'coming-soon';

interface LiveFeature {
  id: string;
  name: string;
  tagline: string;
  description: string;
  icon: ReactNode;
  status: FeatureStatus;
  docsSlug: string;
}

const FEATURES: LiveFeature[] = [
  {
    id: 'presence',
    name: 'Presence',
    tagline: 'See who else is here, in real time.',
    description:
      'Tenant-scoped Phoenix Channels with Phoenix.Presence. Track who is connected to a room and broadcast lightweight awareness updates (cursors, status, typing).',
    icon: <Radio size={18} aria-hidden="true" />,
    status: 'alpha',
    docsSlug: 'live-presence',
  },
  {
    id: 'state',
    name: 'State',
    tagline: 'Server-authoritative shared state with version+nonce LWW.',
    description:
      'Structured records, file trees, dashboards, whiteboards — anything that benefits from server-side validation and op-level access control.',
    icon: <Database size={18} aria-hidden="true" />,
    status: 'coming-soon',
    docsSlug: 'live-state',
  },
  {
    id: 'doc',
    name: 'Doc',
    tagline: 'CRDT text editing on top of Yjs.',
    description:
      'Free-form collaborative text with first-class CodeMirror, ProseMirror, and Monaco bindings. Phoenix relays binary Yjs sync frames; named snapshots are client-driven.',
    icon: <FileText size={18} aria-hidden="true" />,
    status: 'coming-soon',
    docsSlug: 'live-doc',
  },
];

const STATUS_TONE: Record<FeatureStatus, BadgeTone> = {
  alpha: 'info',
  'coming-soon': 'neutral',
};

const STATUS_LABEL: Record<FeatureStatus, string> = {
  alpha: 'Alpha',
  'coming-soon': 'Coming soon',
};

export function LabsSettings() {
  return (
    <div className="labs-settings">
      <div className="labs-settings__intro">
        <div className="labs-settings__intro-icon" aria-hidden="true">
          <FlaskConical size={18} />
        </div>
        <div>
          <h2 className="labs-settings__title">Labs</h2>
          <p className="labs-settings__lede">
            Early-access primitives from <strong>Pryma Live</strong>. APIs and behavior may change
            before general availability — wire them up behind a flag and expect breakage.
          </p>
        </div>
      </div>

      <div className="labs-settings__grid">
        {FEATURES.map(feature => (
          <FeatureCard key={feature.id} feature={feature} />
        ))}
      </div>
    </div>
  );
}

function FeatureCard({ feature }: { feature: LiveFeature }) {
  const isAlpha = feature.status === 'alpha';

  return (
    <article className="labs-feature">
      <header className="labs-feature__header">
        <span className="labs-feature__icon" aria-hidden="true">
          {feature.icon}
        </span>
        <div className="labs-feature__title-row">
          <h3 className="labs-feature__name">{feature.name}</h3>
          <Badge tone={STATUS_TONE[feature.status]} dot>
            {STATUS_LABEL[feature.status]}
          </Badge>
        </div>
      </header>

      <p className="labs-feature__tagline">{feature.tagline}</p>
      <p className="labs-feature__description">{feature.description}</p>

      <div className="labs-feature__setup">
        {isAlpha ? (
          <p className="labs-feature__setup-empty">
            No project-level configuration yet. Connect with the Pryma Live SDK using your project
            API key — see the docs.
          </p>
        ) : (
          <p className="labs-feature__setup-empty">Setup will appear here when this lands.</p>
        )}
      </div>

      <footer className="labs-feature__footer">
        <a
          href={`/docs/${feature.docsSlug}`}
          target="_blank"
          rel="noreferrer"
          className="ui-button ui-button--secondary ui-button--sm labs-feature__docs-link"
        >
          View docs
          <span className="ui-button__icon" aria-hidden="true">
            <ArrowUpRight size={12} />
          </span>
        </a>
      </footer>
    </article>
  );
}
